import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Printer, RotateCcw, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { InteractiveScaleCard } from "../components/InteractiveScaleCard";
import { isUsClinicalHost } from "../lib/usClinicalHost";
import { useAuth } from "../context/AuthContext";
import { LATAM_FULL_SCALE_CATALOG, LATAM_WORKFLOW_SCALE_IDS } from "../clinical/scales_config_latam";
import { USA_QUESTIONNAIRES, USA_WORKFLOW_ORDER, scoreUsInstrument } from "../clinical/scales_config_usa";
import DemoTooltip from "../components/DemoTooltip";

const LATAM_DEFAULT = LATAM_WORKFLOW_SCALE_IDS;

function buildUsWorkflowScales(flags) {
  const list = [...USA_WORKFLOW_ORDER];
  if (flags.cognitiveConcern) list.push("moca_structure");
  if (flags.deliriumConcern) list.push("cam");
  return [...new Set(list)];
}

function UsaQuestionnaireCard({ questionnaireId, answers, onAnswersChange }) {
  const config = USA_QUESTIONNAIRES[questionnaireId];
  if (!config) return null;
  const update = (key, value) => onAnswersChange({ ...answers, [key]: value });
  const score = scoreUsInstrument(questionnaireId, answers || {});
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">{config.title}</p>
        {config.stem ? <p className="text-xs text-slate-600 mt-1">{config.stem}</p> : null}
        {config.helpText ? <p className="text-xs text-slate-500 mt-0.5">{config.helpText}</p> : null}
      </div>
      {config.items.map((item) => (
        <div key={item.id} className="space-y-2">
          <p className="text-sm text-slate-800">{item.question}</p>
          {config.type === "yes_no" && (
            <div className="flex gap-2">
              <Button size="sm" variant={answers?.[item.id] ? "default" : "outline"} onClick={() => update(item.id, true)}>Yes</Button>
              <Button size="sm" variant={answers?.[item.id] === false ? "default" : "outline"} onClick={() => update(item.id, false)}>No</Button>
            </div>
          )}
          {config.type === "single_choice" && (
            <div className="grid gap-2 md:grid-cols-2">
              {(item.options || config.options || []).map((opt) => (
                <Button key={`${item.id}-${opt.label}`} size="sm" variant={answers?.[item.id] === opt.value ? "default" : "outline"} onClick={() => update(item.id, opt.value)}>
                  {opt.label}
                </Button>
              ))}
            </div>
          )}
          {config.type === "numeric" && (
            <input
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              type="number"
              min={config.min}
              max={config.max}
              value={answers?.[item.id] ?? ""}
              onChange={(e) => update(item.id, e.target.value)}
            />
          )}
        </div>
      ))}
      {Array.isArray(config.extra) && config.extra.map((extra) => (
        <div key={extra.id} className="flex items-center gap-2 text-sm">
          <Checkbox checked={!!answers?.[extra.id]} onCheckedChange={(v) => update(extra.id, !!v)} />
          <span>{extra.label}</span>
        </div>
      ))}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 space-y-1">
        <p><span className="font-semibold">Score:</span> {score.score}</p>
        <p><span className="font-semibold">Result:</span> {score.interpretation || "No result yet."}</p>
        {score.nextStep ? <p><span className="font-semibold">Suggested next step:</span> {score.nextStep}</p> : null}
      </div>
    </div>
  );
}

export default function QuickCGA() {
  if (isUsClinicalHost()) return <UsaQuickCGA />;
  return <LatamQuickCGA />;
}

function LatamQuickCGA() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedScales, setSelectedScales] = useState([...LATAM_WORKFLOW_SCALE_IDS]);
  const [scaleAnswers, setScaleAnswers] = useState({});
  const [currentScaleIndex, setCurrentScaleIndex] = useState(0);
  const currentScaleId = selectedScales[currentScaleIndex];
  const progressApply = selectedScales.length ? Math.round(((currentScaleIndex + 1) / selectedScales.length) * 100) : 0;
  const hasAnyAnswers = useMemo(
    () => Object.values(scaleAnswers || {}).some((answers) => Object.keys(answers || {}).length > 0),
    [scaleAnswers]
  );

  const scaleNameMap = useMemo(
    () =>
      LATAM_FULL_SCALE_CATALOG.reduce((acc, item) => {
        acc[item.id] = item.label;
        return acc;
      }, {}),
    []
  );

  const toggleScale = (scaleId) => {
    setSelectedScales((prev) => {
      if (prev.includes(scaleId)) return prev.filter((id) => id !== scaleId);
      return [...prev, scaleId];
    });
  };

  const selectAllScales = () => setSelectedScales([...LATAM_WORKFLOW_SCALE_IDS]);
  const clearScales = () => setSelectedScales([]);

  const handleStart = () => {
    if (selectedScales.length === 0) {
      toast.error("Seleccione al menos una escala para continuar.");
      return;
    }
    setCurrentScaleIndex(0);
    setStep(2);
  };

  const handleNext = () => {
    if (currentScaleIndex < selectedScales.length - 1) {
      setCurrentScaleIndex((v) => v + 1);
      return;
    }
    setStep(3);
  };

  const handleRestart = () => {
    setStep(1);
    setSelectedScales([...LATAM_WORKFLOW_SCALE_IDS]);
    setScaleAnswers({});
    setCurrentScaleIndex(0);
  };

  const handleSave = () => {
    if (user?.email === "demo@aurorageriatrica.com") { toast.error("El demo es de solo lectura. Crea tu cuenta para guardar evaluaciones."); return; }
    localStorage.setItem(
      "aurora_vgi_quick_last",
      JSON.stringify({
        created_at: new Date().toISOString(),
        scales: selectedScales,
        answers: scaleAnswers,
        source: "vgi_quick_latam",
      })
    );
    toast.success("Evaluación guardada localmente.");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="relative mb-4">
          <DemoTooltip message="La VGI Rápida evalúa a tu paciente geriátrico en menos de 5 minutos. Incluye escalas de cognición, funcionalidad, caídas y fragilidad con interpretación automática." position="bottom" />
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">Evaluación rápida VGI</Badge>
        </div>

        {step === 1 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-slate-900">Evaluación rápida (VGI)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Seleccione libremente las escalas que desea aplicar. Puede marcar una, varias o todas.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="border-slate-200" onClick={selectAllScales}>Seleccionar todas</Button>
                <Button variant="outline" className="border-slate-200" onClick={clearScales}>Limpiar selección</Button>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {LATAM_FULL_SCALE_CATALOG.map((scale) => {
                  const checked = selectedScales.includes(scale.id);
                  return (
                    <button
                      key={scale.id}
                      type="button"
                      onClick={() => toggleScale(scale.id)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        checked ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox checked={checked} className="mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{scale.label}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-600">Escalas seleccionadas: {selectedScales.length}</p>
              <div className="flex justify-end">
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleStart}>
                  Siguiente
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-slate-900">Aplicación de escalas</CardTitle>
                <Badge variant="outline" className="border-slate-200">
                  {selectedScales.length === 0 ? "0 de 0" : `${currentScaleIndex + 1} de ${selectedScales.length}`}
                </Badge>
              </div>
              <Progress value={progressApply} className="h-2 bg-slate-200 [&>div]:bg-sky-600" />
            </CardHeader>
            <CardContent className="space-y-4">
              {currentScaleId ? (
                <InteractiveScaleCard
                  key={currentScaleId}
                  scaleId={currentScaleId}
                  scaleName={scaleNameMap[currentScaleId] || currentScaleId}
                  answers={scaleAnswers[currentScaleId] || {}}
                  onAnswersChange={(newAnswers) =>
                    setScaleAnswers((prev) => ({ ...prev, [currentScaleId]: newAnswers }))
                  }
                  initiallyExpanded
                />
              ) : (
                <p className="text-sm text-slate-500">No hay escalas seleccionadas.</p>
              )}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={() => setCurrentScaleIndex((i) => Math.max(0, i - 1))}
                  disabled={currentScaleIndex === 0}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Anterior
                </Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleNext}>
                  {currentScaleIndex === selectedScales.length - 1 ? "Finalizar" : "Siguiente"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl text-slate-900">Informe rápido VGI</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Resumen de escalas seleccionadas y respuestas registradas.</p>
                </div>
                <Badge className="border-emerald-200 bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  Completo
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs text-slate-500">Escalas aplicadas</p>
                  <p className="text-xl font-semibold text-slate-900">{selectedScales.length}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs text-slate-500">Escalas con respuestas</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {selectedScales.filter((id) => Object.keys(scaleAnswers[id] || {}).length > 0).length}
                  </p>
                </div>
              </div>
              <Separator />
              {selectedScales.map((scaleId) => (
                <InteractiveScaleCard
                  key={`result-${scaleId}`}
                  scaleId={scaleId}
                  scaleName={scaleNameMap[scaleId] || scaleId}
                  answers={scaleAnswers[scaleId] || {}}
                  onAnswersChange={(newAnswers) => setScaleAnswers((prev) => ({ ...prev, [scaleId]: newAnswers }))}
                />
              ))}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" className="border-slate-200" onClick={handleRestart}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reiniciar
                </Button>
                <Button variant="outline" className="border-slate-200" onClick={handleSave}>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} disabled={!hasAnyAnswers}>
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimir informe
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function UsaQuickCGA() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [flags, setFlags] = useState({
    cognitiveConcern: false,
    deliriumConcern: false,
    functionalConcern: false,
    fallConcern: false,
    frailtyConcern: false,
    sdohFood: false,
    sdohHousing: false,
    sdohTransport: false,
    sdohUtilities: false,
    sdohSafety: false,
  });
  const [selectedScales, setSelectedScales] = useState(buildUsWorkflowScales({}));
  const [scaleAnswers, setScaleAnswers] = useState({});
  const [currentScaleIndex, setCurrentScaleIndex] = useState(0);
  const currentScaleId = selectedScales[currentScaleIndex];
  const progressApply = selectedScales.length ? Math.round(((currentScaleIndex + 1) / selectedScales.length) * 100) : 0;
  const hasAnyAnswers = useMemo(() => Object.values(scaleAnswers || {}).some((answers) => Object.keys(answers || {}).length > 0), [scaleAnswers]);
  const sdohPositive = [flags.sdohFood, flags.sdohHousing, flags.sdohTransport, flags.sdohUtilities, flags.sdohSafety].filter(Boolean).length;

  const handleStart = () => {
    const autoScales = buildUsWorkflowScales(flags);
    setSelectedScales(autoScales);
    setCurrentScaleIndex(0);
    setStep(2);
  };

  const handleNext = () => {
    if (currentScaleIndex < selectedScales.length - 1) return setCurrentScaleIndex((v) => v + 1);
    setStep(3);
  };

  const handleRestart = () => {
    setStep(1);
    setSelectedScales(buildUsWorkflowScales({}));
    setScaleAnswers({});
    setCurrentScaleIndex(0);
  };

  const saveSnapshot = () => {
    if (user?.email === "demo@aurorageriatrica.com") { toast.error("El demo es de solo lectura. Crea tu cuenta para guardar evaluaciones."); return; }
    localStorage.setItem("aurora_vgi_quick_last", JSON.stringify({ created_at: new Date().toISOString(), scales: selectedScales, answers: scaleAnswers, source: "usa_quick_clinical_workflow" }));
    toast.success("Clinical workflow snapshot saved.");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">USA Quick Clinical Workflow</Badge>
        </div>

        {step === 1 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-slate-900">CMS/STEADI/4Ms Guided Workflow</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <>
                {[
                  "1. What Matters",
                  "2. Medication Review",
                  "3. Functional Status",
                  "4. Cognitive Screening",
                  "5. Fall Risk (STEADI)",
                  "6. Mood",
                  "7. Frailty",
                  "8. Nutrition",
                  "9. Social Risk (SDOH)",
                ].map((item) => (
                  <div key={item} className="rounded-lg border border-slate-200 bg-white p-3 text-sm font-medium text-slate-800">{item}</div>
                ))}
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                  <p className="text-sm font-semibold text-slate-900">Clinical escalation controls</p>
                  <div className="grid gap-2 md:grid-cols-2 text-sm">
                    <label className="flex items-center gap-2"><Checkbox checked={flags.cognitiveConcern} onCheckedChange={(v) => setFlags((f) => ({ ...f, cognitiveConcern: !!v }))} />Cognitive concern present (trigger MoCA)</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.deliriumConcern} onCheckedChange={(v) => setFlags((f) => ({ ...f, deliriumConcern: !!v }))} />Acute confusion concern (trigger CAM)</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.fallConcern} onCheckedChange={(v) => setFlags((f) => ({ ...f, fallConcern: !!v }))} />Recent falls or gait concern (STEADI escalation)</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.functionalConcern} onCheckedChange={(v) => setFlags((f) => ({ ...f, functionalConcern: !!v }))} />Functional dependence concern</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.frailtyConcern} onCheckedChange={(v) => setFlags((f) => ({ ...f, frailtyConcern: !!v }))} />Frailty concern</label>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                  <p className="text-sm font-semibold text-slate-900">Social Risk Screening (SDOH)</p>
                  <div className="grid gap-2 md:grid-cols-2 text-sm">
                    <label className="flex items-center gap-2"><Checkbox checked={flags.sdohFood} onCheckedChange={(v) => setFlags((f) => ({ ...f, sdohFood: !!v }))} />Food insecurity</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.sdohHousing} onCheckedChange={(v) => setFlags((f) => ({ ...f, sdohHousing: !!v }))} />Housing instability</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.sdohTransport} onCheckedChange={(v) => setFlags((f) => ({ ...f, sdohTransport: !!v }))} />Transportation barriers</label>
                    <label className="flex items-center gap-2"><Checkbox checked={flags.sdohUtilities} onCheckedChange={(v) => setFlags((f) => ({ ...f, sdohUtilities: !!v }))} />Utility difficulties</label>
                    <label className="flex items-center gap-2 md:col-span-2"><Checkbox checked={flags.sdohSafety} onCheckedChange={(v) => setFlags((f) => ({ ...f, sdohSafety: !!v }))} />Safety/support needs</label>
                  </div>
                </div>
              </>
              <div className="flex justify-end">
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleStart}>
                  Proceed to Guided Clinical Review
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-slate-900">Guided tools in clinical sequence</CardTitle>
                <Badge variant="outline" className="border-slate-200">{currentScaleIndex + 1} of {selectedScales.length}</Badge>
              </div>
              <Progress value={progressApply} className="h-2 bg-slate-200 [&>div]:bg-sky-600" />
            </CardHeader>
            <CardContent className="space-y-4">
              {currentScaleId ? (
                <UsaQuestionnaireCard
                  questionnaireId={currentScaleId}
                  answers={scaleAnswers[currentScaleId] || {}}
                  onAnswersChange={(newAnswers) => setScaleAnswers((prev) => ({ ...prev, [currentScaleId]: newAnswers }))}
                />
              ) : null}
              <div className="flex justify-between">
                <Button variant="outline" className="border-slate-200" onClick={() => setCurrentScaleIndex((i) => Math.max(0, i - 1))} disabled={currentScaleIndex === 0}>
                  <ArrowLeft className="mr-2 h-4 w-4" />Previous
                </Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleNext}>
                  {currentScaleIndex === selectedScales.length - 1 ? "View Clinical Workflow Summary" : "Next"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl text-slate-900">USA Guided Clinical Workflow Summary</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Clinical screening summary with consistent scoring language and suggested follow-up actions.</p>
                </div>
                <Badge className="border-emerald-200 bg-emerald-100 text-emerald-700"><CheckCircle2 className="mr-1 h-4 w-4" />Complete</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-3"><p className="text-xs text-slate-500">Guided tools used</p><p className="text-xl font-semibold text-slate-900">{selectedScales.length}</p></div>
                <div className="rounded-lg border border-slate-200 bg-white p-3"><p className="text-xs text-slate-500">Tool responses captured</p><p className="text-xl font-semibold text-slate-900">{selectedScales.filter((id) => Object.keys(scaleAnswers[id] || {}).length > 0).length}</p></div>
                <div className="rounded-lg border border-slate-200 bg-white p-3"><p className="text-xs text-slate-500">SDOH positives</p><p className="text-xl font-semibold text-slate-900">{sdohPositive}</p></div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-800">Final workflow sequence</p>
                <p className="text-xs text-slate-600">What Matters -> Medication -> Functional -> Cognition -> Fall Risk (STEADI) -> Mood -> Frailty -> Nutrition -> Social Risk (SDOH).</p>
              </div>
              {selectedScales.map((scaleId) => (
                <UsaQuestionnaireCard
                  key={`result-${scaleId}`}
                  questionnaireId={scaleId}
                  answers={scaleAnswers[scaleId] || {}}
                  onAnswersChange={(newAnswers) => setScaleAnswers((prev) => ({ ...prev, [scaleId]: newAnswers }))}
                />
              ))}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" className="border-slate-200" onClick={handleRestart}><RotateCcw className="mr-2 h-4 w-4" />New snapshot</Button>
                <Button variant="outline" className="border-slate-200" onClick={saveSnapshot}><Save className="mr-2 h-4 w-4" />Save snapshot</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} disabled={!hasAnyAnswers}><Printer className="mr-2 h-4 w-4" />Print summary</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
